import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { LanguageSwitcher } from '../components/i18n/LanguageSwitcher'
import { useI18n } from '../i18n'
import type { MessageKey } from '../i18n/messages'
import pageStyles from './UserManual.module.css'
import styles from './CloudSync.module.css'

const FEEDBACK_EMAIL = 'developers@the-fortitudes.com'

const FREE_KEYS: MessageKey[] = [
  'cloudSync.freeItem1',
  'cloudSync.freeItem2',
  'cloudSync.freeItem3',
  'cloudSync.freeItem4',
]

const PAID_KEYS: MessageKey[] = [
  'cloudSync.paidItem1',
  'cloudSync.paidItem2',
  'cloudSync.paidItem3',
  'cloudSync.paidItem4',
]

const HOW_KEYS: MessageKey[] = [
  'cloudSync.howStep1',
  'cloudSync.howStep2',
  'cloudSync.howStep3',
]

type Plan = {
  nameKey: MessageKey
  priceKey: MessageKey
  periodKey: MessageKey
  noteKey?: MessageKey
  highlight?: boolean
}

const PLANS: Plan[] = [
  {
    nameKey: 'cloudSync.planMonthlyName',
    priceKey: 'cloudSync.planMonthlyPrice',
    periodKey: 'cloudSync.planMonthlyPeriod',
  },
  {
    nameKey: 'cloudSync.planHalfYearName',
    priceKey: 'cloudSync.planHalfYearPrice',
    periodKey: 'cloudSync.planHalfYearPeriod',
    noteKey: 'cloudSync.planHalfYearNote',
  },
  {
    nameKey: 'cloudSync.planYearlyName',
    priceKey: 'cloudSync.planYearlyPrice',
    periodKey: 'cloudSync.planYearlyPeriod',
    noteKey: 'cloudSync.planYearlyNote',
    highlight: true,
  },
]

export function CloudSync() {
  const { t } = useI18n()
  const waitlistMailto = `mailto:${FEEDBACK_EMAIL}?subject=${encodeURIComponent(t('cloudSync.waitlistSubject'))}&body=${encodeURIComponent(t('cloudSync.waitlistEmailBody'))}`

  useEffect(() => {
    const previousTitle = document.title
    document.title = t('cloudSync.documentTitle')
    window.scrollTo(0, 0)
    return () => {
      document.title = previousTitle
    }
  }, [t])

  return (
    <article className={pageStyles.page}>
      <header className={pageStyles.hero}>
        <div className={pageStyles.heroAtmosphere} aria-hidden="true">
          <span className={pageStyles.washA} />
          <span className={pageStyles.washB} />
          <span className={pageStyles.washC} />
          <span className={pageStyles.silk} />
        </div>
        <div className={pageStyles.heroInner}>
          <div className={pageStyles.heroTop}>
            <Link to="/" className={pageStyles.backLink}>
              {t('cloudSync.backToLibrary')}
            </Link>
            <LanguageSwitcher />
          </div>
          <p className={pageStyles.eyebrow}>{t('cloudSync.eyebrow')}</p>
          <h1 className={pageStyles.title}>{t('cloudSync.title')}</h1>
          <p className={pageStyles.subtitle}>{t('cloudSync.subtitle')}</p>
        </div>
      </header>

      <div className={pageStyles.body}>
        <div className={styles.panel}>
          <p className={styles.statusBadge}>{t('cloudSync.status')}</p>

          <section className={styles.block} aria-labelledby="compare-heading">
            <h2 id="compare-heading" className={styles.blockTitle}>
              {t('cloudSync.compareTitle')}
            </h2>
            <p className={styles.blockLead}>{t('cloudSync.compareLead')}</p>
            <div className={styles.compareGrid}>
              <div className={styles.compareCol}>
                <h3 className={styles.compareHeading}>{t('cloudSync.freeTitle')}</h3>
                <ul className={styles.compareList}>
                  {FREE_KEYS.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </div>
              <div className={`${styles.compareCol} ${styles.compareColPaid}`}>
                <h3 className={styles.compareHeading}>{t('cloudSync.paidTitle')}</h3>
                <ul className={styles.compareList}>
                  {PAID_KEYS.map((key) => (
                    <li key={key}>{t(key)}</li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className={styles.block} aria-labelledby="not-included-heading">
            <h2 id="not-included-heading" className={styles.blockTitle}>
              {t('cloudSync.notIncludedTitle')}
            </h2>
            <p className={styles.blockLead}>{t('cloudSync.notIncludedBody')}</p>
          </section>

          <section className={styles.block} aria-labelledby="how-heading">
            <h2 id="how-heading" className={styles.blockTitle}>
              {t('cloudSync.howTitle')}
            </h2>
            <p className={styles.blockLead}>{t('cloudSync.howLead')}</p>
            <ol className={styles.steps}>
              {HOW_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ol>
          </section>

          <section className={styles.block} aria-labelledby="pricing-heading">
            <h2 id="pricing-heading" className={styles.blockTitle}>
              {t('cloudSync.pricingTitle')}
            </h2>
            <p className={styles.blockLead}>{t('cloudSync.pricingLead')}</p>
            <div className={styles.priceBoard} aria-label={t('cloudSync.pricingTitle')}>
              <div className={styles.priceBoardGrid}>
                {PLANS.map((plan) => (
                  <div
                    key={plan.nameKey}
                    className={`${styles.pricePlan} ${plan.highlight ? styles.pricePlanHighlight : ''}`}
                  >
                    <p className={styles.pricePlanName}>{t(plan.nameKey)}</p>
                    <p className={styles.pricePlanAmount}>
                      <span className={styles.pricePlanValue}>{t(plan.priceKey)}</span>
                      <span className={styles.pricePlanPeriod}>{t(plan.periodKey)}</span>
                    </p>
                    {plan.noteKey ? (
                      <p className={styles.pricePlanNote}>{t(plan.noteKey)}</p>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            <p className={styles.pricingPhilosophy}>{t('cloudSync.pricingPhilosophy')}</p>
          </section>

          <section className={styles.blockLast} aria-labelledby="waitlist-heading">
            <h2 id="waitlist-heading" className={styles.blockTitle}>
              {t('cloudSync.waitlistTitle')}
            </h2>
            <p className={styles.blockLead}>{t('cloudSync.waitlistBody')}</p>
            <a className={styles.cta} href={waitlistMailto}>
              {t('cloudSync.waitlistCta')}
              <span className={styles.ctaEmail}>{FEEDBACK_EMAIL}</span>
            </a>
          </section>
        </div>

        <div className={pageStyles.footerLinks}>
          <Link to="/">{t('cloudSync.backToLibrary')}</Link>
          <Link to="/data">{t('cloudSync.openData')}</Link>
          <Link to="/disclaimer">{t('cloudSync.openDisclaimer')}</Link>
        </div>
      </div>
    </article>
  )
}
